import { useState, useEffect, useCallback, useRef } from "react";
import { X, MagnifyingGlass, DownloadSimple, CaretLeft, CaretRight, SpinnerGap, ArrowSquareOut, PencilSimple, Check, Lightning, Sparkle, ArrowClockwise, Buildings, Info } from "@phosphor-icons/react";
import { ingestReconSummary, ingestReconRows, ingestReconExport, ingestReconPatchRow, adminCollegesLiteSearch, ingestReconApply, ingestReconRediff, ingestReconStates, ingestReconLlmMatch, ingestReconAcceptSuggestions, ingestReconCreateNew, ingestReconCreateNewPreview, runFillNewColleges, jobsStatus, ingestReconClassifyStateLlmRow } from "../lib/api";
import { toast } from "sonner";

const FEE_HEAD = { govt_fees: "Govt", private_fees: "Private", other_fees: "Mgmt/Other", nri_fees: "NRI" };
const FEE_COLS = ["govt_fees", "private_fees", "other_fees", "nri_fees"];
const TABS = [
  { key: "all", label: "All" },
  { key: "matched", label: "Matched" },
  { key: "unmatched", label: "Unmatched" },
  { key: "changed", label: "Data Update" },
  { key: "new", label: "Data New" },
  { key: "applied", label: "Applied" },
  { key: "created", label: "Created New" },
  { key: "conflict", label: "Data Conflict" },
];
const fmtNum = (v) => (v === null || v === undefined || v === "") ? "" : (typeof v === "number" ? v.toLocaleString("en-IN") : String(v));

// Tiny relative time ("now" / "5m" / "2h" / "3d") for the applied-row timestamp.
const relTime = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

// Mirror of backend _current_academic_year() — for the empty-year fallback hint.
const currentAcademicYear = () => {
  const d = new Date();
  const base = d.getMonth() + 1 >= 6 ? d.getFullYear() : d.getFullYear() - 1;
  return `${base}-${String((base + 1) % 100).padStart(2, "0")}`;
};
const CUR_YEAR = currentAcademicYear();

const STATUS_LABEL = { changed: "data update", new: "data new", unchanged: "unchanged", applied: "applied", unmatched: "unmatched" };
const STATUS_STYLE = {
  unmatched: "bg-rose-100 text-rose-700",
  changed: "bg-amber-100 text-amber-700",
  new: "bg-blue-100 text-blue-700",
  unchanged: "bg-emerald-100 text-emerald-700",
  applied: "bg-violet-100 text-violet-700",
};
const METHOD_STYLE = { fuzzy: "bg-amber-100 text-amber-700", manual: "bg-teal-100 text-teal-700" };

export default function IngestReconViewer({ ingestId, filename, onClose, onSummaryChange, onApplied }) {
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const pageSize = 50;
  const debounceRef = useRef(null);
  const [edits, setEdits] = useState({});   // row_no -> {overrides:{field:val|null}, skipped:bool}
  const [drag, setDrag] = useState(null);   // {rowNo, field, value} while dragging
  const [courseF, setCourseF] = useState(null);
  const [yearF, setYearF] = useState(null);
  const [statesList, setStatesList] = useState([]);
  const [stateF, setStateF] = useState("");   // header State filter (surface rows of one state)
  useEffect(() => { ingestReconStates().then((r) => setStatesList(r.data?.states || [])).catch(() => {}); }, []);
  const [sel, setSel] = useState(new Set());        // selected column checkboxes (drives writes + column-aware tabs)
  const colsParam = [...sel].sort().join(",");      // stable key for effect deps + query param

  const loadSummary = useCallback(async () => {
    try {
      const r = await ingestReconSummary(ingestId, colsParam || undefined);
      setSummary(r.data);
      if (onSummaryChange) onSummaryChange(r.data);
    } catch (e) {}
  }, [ingestId, onSummaryChange, colsParam]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Quietly re-diff against current college data on open, so an old ingest's tabs
  // (Data Update / New / Unchanged) are always fresh — then refresh summary + rows.
  const [rediffing, setRediffing] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRediffing(true);
      try { await ingestReconRediff(ingestId); } catch (e) {}
      if (!cancelled) { await loadSummary(); await load(); }
      if (!cancelled) setRediffing(false);
    })();
    return () => { cancelled = true; };
  }, [ingestId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ingestReconRows(ingestId, { page, page_size: pageSize, status: tab, q: q || undefined, course: courseF || undefined, year: yearF || undefined, cols: colsParam || undefined, state: stateF || undefined });
      setData(r.data);
      setEdits((prev) => {
        const next = { ...prev };
        for (const row of (r.data.rows || [])) {
          if (next[row.row_no] === undefined) {
            next[row.row_no] = { overrides: row.overrides || {}, skipped: !!row.skipped };
          }
        }
        return next;
      });
    } catch (e) { setData({ rows: [], total: 0 }); }
    setLoading(false);
  }, [ingestId, page, tab, q, courseF, yearF, colsParam, stateF]);

  useEffect(() => { load(); }, [load]);

  // debounce search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setQ(qInput.trim()); }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [qInput]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  const [applying, setApplying] = useState(false);
  const selInitRef = useRef(false);
  useEffect(() => {
    const pf = summary?.present_fields;
    if (!pf || selInitRef.current) return;   // initialize the checkboxes ONCE from present_fields
    selInitRef.current = true;
    const s = new Set();
    if (pf.fees) ["govt_fees", "private_fees", "other_fees", "nri_fees"].forEach((x) => s.add(x));
    if (pf.seats) s.add("seat_intake");
    if (pf.established_year) s.add("established_year");
    if (pf.college_status) s.add("college_status");
    setSel(s);
  }, [summary?.present_fields]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [colsParam]);   // reset to page 1 when the column selection changes
  const toggleCol = (k) => setSel((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  // Single, column-aware row status (Option A): pending update/new wins over applied,
  // applied wins over unchanged. Driven by the ticked columns (same as the tabs).
  const rowStatus = (r) => {
    if (!r.resolved_code || r.status === "unmatched") return "unmatched";
    // NMC SM rows carry a real seat-intake diff (new/changed/unchanged) that isn't gated on a
    // ticked fee column, so surface it directly. Other SM reconcilers never set these, so they
    // fall through to the existing logic unchanged.
    if (isSM && (r.status === "new" || r.status === "changed")) return r.status;
    const inSel = (arr) => (arr || []).some((c) => sel.has(c));
    if (inSel(r.changed_fields)) return "changed";
    if (inSel(r.new_fields)) return "new";
    if (r.applied) return "applied";
    return "unchanged";
  };
  const unmatchedCount = (summary?.by_status?.unmatched) || 0;
  const [applyScope, setApplyScope] = useState(null); // which scope is currently applying
  const applyAll = async (scope = null) => {
    const cols = [...sel];
    if (!cols.length) { toast.error("Select at least one column to apply"); return; }
    const scopeLabel = scope ? (TABS.find((t) => t.key === scope)?.label || scope) : null;
    const msg = scope
      ? `Apply ${cols.length} column(s) to the ${scopeLabel} rows only (${tabCount(scope)})? This updates the college master directly.`
      : `Apply ${cols.length} column(s) to matched colleges, skipping unmatched? This updates the college master directly.`;
    if (!window.confirm(msg)) return;
    setApplying(true); setApplyScope(scope);
    try {
      const fd = new FormData();
      fd.append("columns", cols.join(","));
      fd.append("create_new", "false");
      if (scope) fd.append("scope", scope);
      const { data: res } = await ingestReconApply(ingestId, fd);
      setApplyResult(res);
      if (onApplied) onApplied(ingestId, res);
      toast.success(`Applied${scopeLabel ? ` (${scopeLabel})` : ""} — ${res.fees_updated || 0} updated${res.fees_new ? `, ${res.fees_new} new` : ""}`);
      await loadSummary();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setApplying(false); setApplyScope(null);
  };

  const [applyResult, setApplyResult] = useState(null);   // persist last apply outcome (written count)
  const isAllot = summary?.mode === "allotment" || summary?.mode === "orphan" || summary?.mode === "sm_review";
  const isOrphan = summary?.mode === "orphan" || summary?.mode === "sm_review";
  const isSM = summary?.mode === "sm_review";
  const [remapRow, setRemapRow] = useState(null);   // row_no of the matched SM row being re-mapped
  // Allotment reconcile: no fee columns — just resolve names → Apply all matched (restamp
  // raw_allotments + rebuild cutoffs via the existing assign-unmatched core).
  const applyAllot = async () => {
    // SM reconciler: apply only the PENDING writes (newly-resolved / created / re-mapped rows);
    // auto-matched rows already have their seats in the DB. Allotment/orphan keep applying all matched.
    const pend = summary?.pending ?? 0;
    const msg = isSM
      ? `Apply ${pend} pending seat-intake row(s)? This writes the ingested seats onto the resolved colleges (moving re-mapped ones collision-safely). Skipped rows are skipped.`
      : "Apply all matched allotment rows? This restamps raw_allotments and rebuilds cutoffs. Skipped rows are skipped.";
    if (!window.confirm(msg)) return;
    setApplying(true);
    try {
      const fd = new FormData();
      if (isSM) fd.append("scope", "pending");
      const { data: res } = await ingestReconApply(ingestId, fd);
      setApplyResult(res);
      if (onApplied) onApplied(ingestId, res);
      toast.success(isSM
        ? `Applied ${res.applied || 0} colleges · ${res.seats_written || 0} seat rows written${res.seats_removed ? ` · ${res.seats_removed} re-mapped` : ""}`
        : `Applied ${res.applied || 0} colleges · ${res.restamped_rows || 0} allotment rows restamped`);
      await loadSummary(); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setApplying(false);
  };

  const bs = summary?.by_status || {};
  const bm = summary?.by_method || {};
  const pf = summary?.present_fields || { fees: true };
  const showYC = summary?.show_year_course ?? true;
  const showFees = pf.fees !== false && (pf.fees || !summary);
  const showSeats = !!pf.seats;
  const showEst = !!pf.established_year;
  const showStatus = !!pf.college_status;
  const total = summary?.total ?? 0;
  const unmatchedN = summary?.unmatched ?? 0;
  // applied rows (post-apply) still count as matched; changed/new absorb them by their original bucket
  const matchedN = total - unmatchedN;
  const tabCount = (key) => (isAllot ? ({
    all: total,
    matched: summary?.matched || 0,
    pending: summary?.pending || 0,
    unmatched: summary?.unmatched || 0,
    created: summary?.created || 0,
    applied: summary?.applied || 0,
  }[key] ?? 0) : ({
    all: total,
    matched: matchedN,
    unmatched: unmatchedN,
    changed: (bs.changed || 0),
    new: bs.new || 0,
    applied: summary?.applied || 0,
    created: summary?.created || 0,
    conflict: summary?.conflict || 0,
  }[key] ?? 0));
  const ALLOT_TABS = [
    { key: "all", label: "All" },
    { key: "matched", label: "Matched" },
    { key: "unmatched", label: "Unmatched" },
    { key: "created", label: "Created New" },
    { key: "applied", label: "Applied" },
  ];
  // SM reconciler: add a "Pending" tab (resolved-but-not-applied writes) so the operator can
  // eyeball what Apply will write and modify/clear each row before committing.
  const SM_TABS = [
    { key: "all", label: "All" },
    { key: "matched", label: "Matched" },
    { key: "pending", label: "Pending" },
    { key: "unmatched", label: "Unmatched" },
    { key: "created", label: "Created New" },
    { key: "applied", label: "Applied" },
  ];
  const tabs = isSM ? SM_TABS : isAllot ? ALLOT_TABS : TABS;

  const courseFacets = Object.entries(summary?.by_course || {}).sort((a, b) => b[1] - a[1]);
  const yearFacets = Object.entries(summary?.by_year || {}).sort((a, b) => (a[0] < b[0] ? 1 : -1));

  // Export honours the CURRENT view (status/method tab + search + course + year)
  const doExport = async () => {
    try {
      const params = {};
      if (tab && tab !== "all") params.status = tab;
      if (q) params.q = q;
      if (courseF) params.course = courseF;
      if (yearF) params.year = yearF;
      const res = await ingestReconExport(ingestId, params);
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
      const a = document.createElement("a");
      a.href = url; a.download = `recon_${ingestId.slice(0, 12)}_${tab}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {}
  };

  const hasVal = (v) => v !== null && v !== undefined && v !== "";
  // Effective cell value = per-row override for this field if set, else the raw input value.
  const cellVal = (row, field) => {
    const ov = edits[row.row_no]?.overrides;
    if (ov && Object.prototype.hasOwnProperty.call(ov, field)) return ov[field];
    return (row.input || {})[field];
  };
  const isSkipped = (row) => !!edits[row.row_no]?.skipped;

  const persistRow = async (rowNo, patch) => {
    try {
      const res = await ingestReconPatchRow(ingestId, rowNo, patch);
      const u = res.data;
      if (u) setData((d) => ({ ...d, rows: (d.rows || []).map((x) =>
        x.row_no === rowNo ? { ...x, status: u.status, changed_fields: u.changed_fields, new_fields: u.new_fields } : x) }));
      loadSummary();   // edit may change match/unmatched + status counts → refresh chips/tabs/card
    } catch (e) {}
  };
  const moveCell = (row, fromField, toField) => {
    if (fromField === toField) return;
    if (hasVal(cellVal(row, toField))) return;          // blank target only
    const val = cellVal(row, fromField);
    if (!hasVal(val)) return;
    const cur = edits[row.row_no]?.overrides || {};
    const nextOv = { ...cur, [fromField]: null, [toField]: val };
    setEdits((p) => ({ ...p, [row.row_no]: { ...(p[row.row_no] || {}), overrides: nextOv } }));
    persistRow(row.row_no, { overrides: nextOv });
    setDrag(null);
  };
  const toggleSkip = (row) => {
    const next = !isSkipped(row);
    setEdits((p) => ({ ...p, [row.row_no]: { ...(p[row.row_no] || {}), skipped: next } }));
    persistRow(row.row_no, { skipped: next });
  };
  // Inline text edit for course / year — persists as an override on the recon row.
  const setField = (row, field, raw) => {
    const value = (raw ?? "").trim();
    if (value === (cellVal(row, field) ?? "")) return;   // no change
    const nextOv = { ...(edits[row.row_no]?.overrides || {}), [field]: value };
    setEdits((p) => ({ ...p, [row.row_no]: { ...(p[row.row_no] || {}), overrides: nextOv } }));
    persistRow(row.row_no, { overrides: nextOv });
  };
  // Manually assign an unmatched row to an existing college (lite-search picker), or clear it.
  const assignCollege = async (row, picked) => {    try {
      const res = await ingestReconPatchRow(ingestId, row.row_no, {
        manual: picked ? { college_code: picked.college_code, name: picked.primary_name || picked.short_name || picked.name } : null });
      const u = res.data;
      setData((d) => ({ ...d, rows: (d.rows || []).map((x) => x.row_no === row.row_no ? { ...x, ...u } : x) }));
      // manual assign changes match/unmatched counts → refresh chips, tabs & chat card, AND
      // reload the current tab so a just-matched row leaves the Unmatched view (→ Matched tab,
      // where "Apply all matched" picks it up). Mirrors Accept-all's refresh.
      await loadSummary();
      await load();
    } catch (e) {}
  };
  // Set / override the surfaced State on an (unmatched) row.
  const setRowState = async (row, value) => {
    try {
      const res = await ingestReconPatchRow(ingestId, row.row_no, { state: value });
      const u = res.data;
      setData((d) => ({ ...d, rows: (d.rows || []).map((x) => x.row_no === row.row_no ? { ...x, state: u.state, state_source: u.state_source } : x) }));
      // Assigning a state moves this row into that state's bucket → refresh the
      // state-scoped counts (LLM match · State, Create new · State, state filter).
      loadSummary();
    } catch (e) {}
  };
  // Lightweight per-row LLM state lookup (for '— no state —' rows).
  const llmStateForRow = async (row) => {
    try {
      const { data } = await ingestReconClassifyStateLlmRow(ingestId, row.row_no);
      if (data?.ok && data.state) {
        setData((d) => ({ ...d, rows: (d.rows || []).map((x) => x.row_no === row.row_no ? { ...x, state: data.state, state_source: "llm" } : x) }));
        loadSummary();   // same as manual edit — refresh state-scoped counts
        toast.success(`State: ${data.state}`);
      } else {
        toast.error("AI couldn't determine a state — set it manually");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "State lookup failed");
    }
  };
  const dismissSuggestion = async (row, dismissed) => {
    try {
      const res = await ingestReconPatchRow(ingestId, row.row_no, { dismiss_suggestion: dismissed });
      const u = res.data;
      setData((d) => ({ ...d, rows: (d.rows || []).map((x) => x.row_no === row.row_no ? { ...x, suggestion_dismissed: u.suggestion_dismissed } : x) }));
    } catch (e) {}
  };
  const llmMatch = async () => {
    try { await ingestReconLlmMatch(ingestId, stateF || undefined, stateF || undefined); toast.success(stateF ? `LLM matching ${stateF}…` : "LLM matching started…"); loadSummary(); }
    catch (e) { toast.error("Could not start LLM match"); }
  };
  const acceptAll = async () => {
    try { const { data } = await ingestReconAcceptSuggestions(ingestId, stateF || undefined); toast.success(`Accepted ${data.accepted || 0} suggestions${stateF ? ` for ${stateF}` : ""}`); await loadSummary(); await load(); }
    catch (e) { toast.error("Accept all failed"); }
  };
  const [creating, setCreating] = useState(false);
  const [createdCodes, setCreatedCodes] = useState(null);
  const [filling, setFilling] = useState(false);
  const [fillGone, setFillGone] = useState(false);   // hide Fill New once all enriched (with fade)
  const [fillFading, setFillFading] = useState(false);
  const [fillStatuses, setFillStatuses] = useState({});   // code -> job status (queued/running/completed/failed)
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const createNew = async () => {
    setCreating(true);
    try {
      const { data } = await ingestReconCreateNew(ingestId, stateF || undefined);
      const skipped = data.skipped_no_state || 0;
      toast.success(`Created ${data.created || 0} new colleges${skipped ? ` · ${skipped} skipped (no state)` : ""}`);
      setCreatedCodes(data.codes || []);
      setShowPreview(false);
      await loadSummary(); await load();
      if ((data.codes || []).length) { setTab("created"); setPage(1); startFill(data.codes); }
    } catch (e) { toast.error("Create new failed"); }
    finally { setCreating(false); }
  };
  const loadPreview = async () => {
    if (showPreview) { setShowPreview(false); return; }
    try { const { data } = await ingestReconCreateNewPreview(ingestId, stateF || undefined); setPreview(data); setShowPreview(true); }
    catch (e) { toast.error("Could not load code preview"); }
  };
  const startFill = async (codes) => {
    if (!codes || !codes.length) return;
    setFilling(true); setFillGone(false); setFillFading(false);
    try {
      const { data } = await runFillNewColleges(null, codes);
      const jobs = data.jobs || {};
      const init = {}; Object.keys(jobs).forEach((c) => { init[c] = { status: "queued", stage: "" }; });
      setFillStatuses(init);
      toast.success(`Fill-New started for ${data.count ?? codes.length} colleges`);
      const jobIds = Object.values(jobs);
      if (jobIds.length) {
        const poll = setInterval(async () => {
          try {
            const { data: sres } = await jobsStatus(jobIds);
            const smap = sres.statuses || {}; const pmap = sres.progress || {};
            const next = {}; Object.entries(jobs).forEach(([code, jid]) => {
              next[code] = { status: smap[jid] || "queued", stage: (pmap[jid] || {}).stage || "" };
            });
            setFillStatuses(next);
            if (Object.values(next).every((s) => s.status === "completed" || s.status === "failed")) { clearInterval(poll); setFilling(false); load(); setFillFading(true); setTimeout(() => setFillGone(true), 700); }
          } catch (e) { /* keep polling */ }
        }, 3000);
      } else { setFilling(false); }
    } catch (e) { toast.error("Fill New failed"); setFilling(false); }
  };
  const fillNew = () => startFill(createdCodes);
  // Poll summary while LLM match is running so the progress label + suggestions refresh.
  const lmRunning = summary?.llm_match?.running;
  useEffect(() => {
    if (!lmRunning) return;
    const t = setInterval(() => { loadSummary(); load(); }, 4000);
    return () => clearInterval(t);
  }, [lmRunning]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[60] bg-white flex flex-col" data-testid="ingest-recon-viewer">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-zinc-200 bg-white shrink-0">
        <div className="min-w-0">
          <h2 className="text-[15px] font-black text-[#09090B] truncate flex items-center gap-2">
            Ingest reconciliation
            {rediffing && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#6043EF] normal-case tracking-normal"
                data-testid="recon-refreshing-hint">
                <SpinnerGap size={12} className="animate-spin" /> refreshing…
              </span>
            )}
          </h2>
          <p className="text-[11px] font-mono text-zinc-400 truncate">{filename || ingestId}</p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0 flex-wrap justify-end">
          <div className="flex items-center gap-2 text-[11px] font-semibold" data-testid="recon-viewer-cols">
            {showFees && [["govt_fees", "Govt"], ["private_fees", "Private"], ["other_fees", "Mgmt/Other"], ["nri_fees", "NRI"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-1 cursor-pointer text-zinc-600">
                <input type="checkbox" checked={sel.has(k)} onChange={() => toggleCol(k)} className="accent-[#6043EF]" />{lbl}
              </label>
            ))}
            {showSeats && <label className="flex items-center gap-1 cursor-pointer text-zinc-700"><input type="checkbox" checked={sel.has("seat_intake")} onChange={() => toggleCol("seat_intake")} className="accent-[#6043EF]" />Seats</label>}
            {showEst && <label className="flex items-center gap-1 cursor-pointer text-zinc-700"><input type="checkbox" checked={sel.has("established_year")} onChange={() => toggleCol("established_year")} className="accent-[#6043EF]" />Est. year</label>}
            {showStatus && <label className="flex items-center gap-1 cursor-pointer text-zinc-700"><input type="checkbox" checked={sel.has("college_status")} onChange={() => toggleCol("college_status")} className="accent-[#6043EF]" />Status</label>}
          </div>
          <button onClick={isAllot ? (() => applyAllot()) : applyAll}
            disabled={applying || (isSM && !(summary?.pending ?? 0))} data-testid="recon-viewer-apply"
            title={isSM ? "Write the pending seat-intake rows (newly-resolved / created / re-mapped) onto their colleges" : undefined}
            className="h-9 px-4 rounded-full flex items-center gap-1.5 text-[13px] font-bold text-white bg-[#6043EF] hover:bg-[#4f34d6] disabled:opacity-50">
            {applying ? <SpinnerGap size={16} className="animate-spin" /> : <Check size={16} weight="bold" />}
            {isSM ? `Apply pending (${summary?.pending ?? 0})` : isAllot ? `Apply all matched (${summary?.matched || 0})` : "Apply"}
          </button>
          {applyResult && (
            <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-50 border border-emerald-200 text-[12px] font-bold text-emerald-700"
              data-testid="recon-viewer-applied-note">
              <Check size={14} weight="bold" />
              {isSM
                ? <>{applyResult.applied || 0} applied · {applyResult.seats_written || 0} seat rows written
                    {applyResult.seats_removed ? <span className="font-normal text-emerald-500">· {applyResult.seats_removed} re-mapped</span> : null}</>
                : isAllot
                ? <>{applyResult.applied || 0} applied · {applyResult.restamped_rows || 0} rows restamped</>
                : <>{((applyResult.fees_updated || 0) + (applyResult.fees_new || 0))} written to database
                    {applyResult.fees_new ? <span className="font-normal text-emerald-500">· {applyResult.fees_new} new</span> : null}</>}
            </span>
          )}
          <button onClick={onClose} data-testid="recon-viewer-close"
            className="h-9 w-9 rounded-full flex items-center justify-center text-zinc-500 hover:bg-zinc-100 shrink-0">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Resolution by method */}
      <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-2.5 border-b border-zinc-200 bg-zinc-50 shrink-0" data-testid="recon-method-chips">
        <span className="text-[9px] font-black uppercase tracking-[0.18em] text-zinc-400 mr-1">Resolved by method</span>
        <Chip label="Total" value={summary?.total ?? "—"} tone="zinc" active={tab === "all"} onClick={() => { setTab("all"); setPage(1); }} testid="recon-chip-all" />
        {!isAllot && <Chip label="Code" value={bm.exact_code || 0} tone="violet" active={tab === "exact_code"} onClick={() => { setTab("exact_code"); setPage(1); }} testid="recon-chip-code" />}
        {!isAllot && <Chip label="Name" value={bm.exact_name || 0} tone="emerald" active={tab === "exact_name"} onClick={() => { setTab("exact_name"); setPage(1); }} testid="recon-chip-name" />}
        {!isAllot && <Chip label="Fuzzy" value={bm.fuzzy || 0} tone="amber" active={tab === "fuzzy"} onClick={() => { setTab("fuzzy"); setPage(1); }} testid="recon-chip-fuzzy" />}
        {isAllot && <Chip label="Matched" value={summary?.matched ?? 0} tone="emerald" active={tab === "matched"} onClick={() => { setTab("matched"); setPage(1); }} testid="recon-chip-matched" />}
        <Chip label="Unmatched" value={summary?.unmatched ?? 0} tone="rose" active={tab === "unmatched"} onClick={() => { setTab("unmatched"); setPage(1); }} testid="recon-chip-unmatched" />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Course / Year facet filters */}
          {(courseFacets.length > 1 || (courseFacets[0] && courseFacets[0][0] !== "—")) && (
            <div className="flex items-center gap-1" data-testid="recon-course-chips">
              <span className="text-[9px] font-black uppercase tracking-[0.16em] text-zinc-400">Course</span>
              {courseFacets.map(([c, n]) => (
                <FacetChip key={c} label={c} count={n} tone="indigo"
                  active={courseF === c} testid={`recon-course-${c}`}
                  onClick={() => { setCourseF(courseF === c ? null : c); setPage(1); }} />
              ))}
            </div>
          )}
          {(yearFacets.length > 1 || (yearFacets[0] && yearFacets[0][0] !== "—")) && (
            <div className="flex items-center gap-1" data-testid="recon-year-chips">
              <span className="text-[9px] font-black uppercase tracking-[0.16em] text-zinc-400">Year</span>
              {yearFacets.map(([y, n]) => (
                <FacetChip key={y} label={y} count={n} tone="sky"
                  active={yearF === y} testid={`recon-year-${y}`}
                  onClick={() => { setYearF(yearF === y ? null : y); setPage(1); }} />
              ))}
            </div>
          )}
          <button onClick={doExport} data-testid="recon-export-current"
            title="Download the rows currently in view (respects the active tab, search, course & year filters)"
            className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#09090B] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-black">
            <DownloadSimple size={12} weight="bold" /> CSV ({data.total ?? 0})
          </button>
        </div>
      </div>

      {/* Tabs (with counts) + search */}
      <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-2.5 border-b border-zinc-200 bg-white shrink-0">
        <div className="flex flex-wrap gap-1">
          {tabs.filter((t) => (t.key !== "conflict" || tabCount("conflict") > 0) && (t.key !== "created" || tabCount("created") > 0)).map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); setPage(1); }}
              data-testid={`recon-tab-${t.key}`}
              className={`h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${
                t.key === "conflict"
                  ? (tab === t.key ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-700 hover:bg-amber-200")
                  : t.key === "created"
                  ? (tab === t.key ? "bg-sky-600 text-white" : "bg-sky-100 text-sky-700 hover:bg-sky-200")
                  : t.key === "pending"
                  ? (tab === t.key ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200")
                  : (tab === t.key ? "bg-[#6043EF] text-white" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200")}`}>
              {t.label} <span className={tab === t.key ? "opacity-80" : "opacity-60"}>({tabCount(t.key)})</span>
            </button>
          ))}
        </div>
        {!isAllot && tab !== "all" && tab !== "applied" && tab !== "unmatched" && tab !== "conflict" && tab !== "created" && tabCount(tab) > 0 && (
          <>
            <span className="h-6 w-px bg-zinc-200 mx-0.5" aria-hidden />
            <button onClick={() => applyAll(tab)} disabled={applying}
              data-testid="recon-apply-scope"
              title={`Write the selected columns to ONLY the ${TABS.find((t) => t.key === tab)?.label || tab} rows (${tabCount(tab)})`}
              className="h-8 pl-2.5 pr-3 rounded-lg flex items-center gap-1.5 text-[11px] font-black text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm shadow-emerald-600/30 disabled:opacity-50 transition-colors">
              {applying && applyScope === tab ? <SpinnerGap size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
              Apply {TABS.find((t) => t.key === tab)?.label || tab} ({tabCount(tab)})
            </button>
          </>
        )}
        <div className="relative ml-auto flex items-center gap-2">
          {tab === "unmatched" && (
            <>
              {(() => {
                const matchedStates = summary?.llm_matched_states || [];
                const cbs = summary?.creatable_by_state || {};
                // Per-state: show while running, or when the selected state still needs a pass
                // (has unmatched rows and hasn't been LLM-matched yet). No filter → show if ANY
                // state still needs matching. Hides for all once every state is done.
                const scopeNeeds = stateF
                  ? ((cbs[stateF] || 0) > 0 && !matchedStates.includes(stateF))
                  : (summary?.states || []).some((s) => (cbs[s] || 0) > 0 && !matchedStates.includes(s));
                if (!(summary?.llm_match?.running || scopeNeeds)) return null;
                return (
                  <button onClick={llmMatch} disabled={summary?.llm_match?.running} data-testid="recon-llm-match"
                    title="Suggest a college for each unmatched row (state-wise LLM)"
                    className="h-8 px-3 rounded-full flex items-center gap-1.5 text-[12px] font-bold text-white bg-[#6043EF] hover:bg-[#4f34d6] disabled:opacity-60">
                    {summary?.llm_match?.running ? <SpinnerGap size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
                    {summary?.llm_match?.running
                      ? `Matching ${summary.llm_match.current || "…"} (${summary.llm_match.done}/${summary.llm_match.total})`
                      : `LLM match (${stateF ? ((cbs[stateF] || 0)) : unmatchedCount})${stateF ? ` · ${stateF}` : ""}`}
                  </button>
                );
              })()}
              {(summary?.suggestions_pending || 0) > 0 && (
                <button onClick={acceptAll} data-testid="recon-accept-all"
                  className="h-8 px-3 rounded-full flex items-center gap-1.5 text-[12px] font-bold text-emerald-700 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100">
                  <Check size={14} weight="bold" /> Accept all suggestions{stateF ? ` (${stateF})` : ""}
                </button>
              )}
              {(() => {
                const createN = stateF ? ((summary?.creatable_by_state || {})[stateF] || 0) : (summary?.creatable || 0);
                if (createN <= 0) return null;
                return (
                <div className="relative flex items-center gap-1">
                  <button onClick={createNew} disabled={creating} data-testid="recon-create-new"
                    title="Create new colleges from unmatched rows that have a state (writes all available values). Stateless rows stay unmatched."
                    className="h-8 px-3 rounded-full flex items-center gap-1.5 text-[12px] font-bold text-amber-700 border border-amber-300 bg-amber-50 hover:bg-amber-100 disabled:opacity-60">
                    {creating ? <SpinnerGap size={14} className="animate-spin" /> : <Buildings size={14} weight="fill" />}
                    Create ({createN}) new{stateF ? ` · ${stateF}` : ""}
                  </button>
                  <button onClick={loadPreview} data-testid="recon-create-new-info" title="Preview the codes that will be minted"
                    className="h-7 w-7 rounded-full flex items-center justify-center text-amber-600 hover:bg-amber-100">
                    <Info size={16} weight="bold" />
                  </button>
                  {showPreview && preview && (
                    <div className="absolute top-9 right-0 z-50 w-72 max-h-80 overflow-auto rounded-lg border border-zinc-200 bg-white shadow-xl p-3" data-testid="recon-create-preview">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold text-zinc-700">Next code per state ({preview.count})</span>
                        <button onClick={() => setShowPreview(false)} className="text-zinc-400 hover:text-zinc-700"><X size={14} /></button>
                      </div>
                      {preview.skipped_no_state > 0 && (
                        <div className="text-[10px] text-amber-600 mb-1.5">{preview.skipped_no_state} row(s) skipped — no state assigned</div>
                      )}
                      <div className="space-y-1">
                        {(preview.states || []).map((s) => (
                          <div key={s.state} className="flex items-center gap-2 text-[11px]">
                            <span className="font-mono font-bold text-[#6043EF] shrink-0">{s.start_code}</span>
                            <span className="text-zinc-600 truncate flex-1">{s.state}</span>
                            <span className="text-zinc-400 shrink-0">×{s.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}
            </>
          )}
          {tab === "created" && createdCodes && createdCodes.length > 0 && !fillGone && (
            <button onClick={fillNew} disabled={filling} data-testid="recon-fill-new"
              title="Enrich the just-created colleges (places, website, media) — same as the Colleges page"
              className={`h-8 px-3 rounded-full flex items-center gap-1.5 text-[12px] font-bold text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-60 transition-all duration-700 ${fillFading ? "opacity-0 scale-90" : "opacity-100 scale-100"}`}>
              {filling ? <SpinnerGap size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
              Fill New ({createdCodes.length})
            </button>
          )}
          {(summary?.states || []).length > 0 && (
            <select value={stateF} onChange={(e) => { setStateF(e.target.value); setPage(1); }}
              data-testid="recon-state-filter" title="Surface rows of one state"
              className={`h-8 rounded-full border px-2.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40 ${stateF ? "border-[#6043EF] text-[#6043EF] bg-[#6043EF]/5 font-bold" : "border-zinc-300 text-zinc-500"}`}>
              <option value="">All states</option>
              {(summary?.states || []).map((s) => <option key={s} value={s}>{s} ({(summary?.creatable_by_state || {})[s] || 0})</option>)}
            </select>
          )}
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search name / code…"
              data-testid="recon-search"
              className="h-8 w-56 pl-8 pr-3 rounded-full border border-zinc-300 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
          </div>
        </div>
      </div>

      {/* Shared autocomplete list for the State editor (canonical states = colleges-page filter) */}
      <datalist id="recon-states-dl">{statesList.map((s) => <option key={s} value={s} />)}</datalist>

      {/* Table */}
      <div className="flex-1 overflow-auto" data-testid="recon-rows-body">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-400 gap-2">
            <SpinnerGap size={20} className="animate-spin" /> Loading…
          </div>
        ) : data.rows.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-zinc-400 text-[13px]">No rows in this view.</div>
        ) : (
          <table className="w-max text-left text-[12px] border-collapse font-['IBM_Plex_Mono']">
            <thead className="sticky top-0 z-10">
              <tr className="bg-zinc-100 text-zinc-600">
                <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">#</th>
                <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Status</th>
                <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Method</th>
                <th className="text-left font-bold px-2.5 py-2 w-[300px]">Resolved</th>
                {tab === "created" && <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap min-w-[150px]">Fill Status</th>}
                <th className="text-left font-bold px-2.5 py-2 align-top w-[320px] min-w-[320px] max-w-[320px]">Input college</th>
                {isSM && <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap min-w-[220px]" title="Seat-matrix category breakdown captured from the ingested document">Seat matrix (categories)</th>}
                {tab === "unmatched" && <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap" title="State (from sheet or auto-classified) — editable; used when creating new colleges">State</th>}
                {showYC && <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Course</th>}
                {showYC && <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Year</th>}
                {showSeats && <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap" title="Top-level college intake (total_seats)">Seats</th>}
                {showEst && <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Est. year</th>}
                {showStatus && <th className="text-left font-bold px-2.5 py-2 whitespace-nowrap">Status</th>}
                {showFees && FEE_COLS.map((c) => <th key={c} className="text-left font-bold px-2.5 py-2 whitespace-nowrap">{FEE_HEAD[c]}</th>)}
                <th className="text-center font-bold px-2.5 py-2 whitespace-nowrap" title="Skip this row — won't be written on Apply">Skip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.rows.map((r) => {
                const inp = r.input || {};
                const unmatched = r.status === "unmatched";
                const skip = isSkipped(r);
                const eff = rowStatus(r);
                return (
                  <tr key={r.row_no} className={skip ? "bg-zinc-100 opacity-60" : (unmatched ? "bg-rose-50/40" : "bg-white hover:bg-zinc-50")}
                    data-testid={`recon-row-${r.row_no}`}>
                    <td className="px-2.5 py-1.5 text-zinc-400">{r.row_no + 1}</td>
                    <td className="px-2.5 py-1.5">
                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${STATUS_STYLE[eff] || "bg-zinc-100 text-zinc-600"}`}
                        data-testid={`recon-status-${r.row_no}`}
                        title={eff === "applied" && (r.applied_columns || []).length ? "Written to DB: " + r.applied_columns.join(", ") : undefined}>
                        {eff === "applied" && <Check size={9} weight="bold" />}
                        {STATUS_LABEL[eff] || eff}
                      </span>
                      {eff === "applied" && r.applied_at && (
                        <span className="ml-1 text-[9px] font-mono text-zinc-400" data-testid={`recon-applied-time-${r.row_no}`}
                          title={new Date(r.applied_at).toLocaleString()}>
                          {relTime(r.applied_at)}
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${METHOD_STYLE[r.method] || "bg-zinc-100 text-zinc-500"}`}>{r.method}</span>
                      {r.status === "unmatched" && r.llm_tried && !(r.suggestion && r.suggestion.code && !r.suggestion_dismissed) && (
                        <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-50 text-violet-500" data-testid={`recon-llm-tried-${r.row_no}`}>llm-tried</span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 w-[300px] max-w-[300px] align-top">
                      {(r.status === "unmatched" && r.method !== "manual") ? (
                        (r.suggestion && r.suggestion.code && !r.suggestion_dismissed) ? (
                          <SuggestionCell sug={r.suggestion}
                            onAccept={() => assignCollege(r, { college_code: r.suggestion.code, name: r.suggestion.name })}
                            onDismiss={() => dismissSuggestion(r, true)}
                            testid={`recon-sug-${r.row_no}`} />
                        ) : (
                          <div className="flex flex-col gap-1">
                            {r.llm_tried && !(r.suggestion && r.suggestion.code) && !r.suggestion_dismissed && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-400" data-testid={`recon-nosug-${r.row_no}`}>
                                no suggestions found
                                <button onClick={() => dismissSuggestion(r, true)} title="Dismiss" data-testid={`recon-nosug-dismiss-${r.row_no}`}
                                  className="w-3.5 h-3.5 rounded-full border border-zinc-300 flex items-center justify-center hover:bg-zinc-100 text-zinc-400">
                                  <X size={9} weight="bold" />
                                </button>
                              </span>
                            )}
                            <ManualAssign onPick={(c) => assignCollege(r, c)} testid={`recon-assign-${r.row_no}`} />
                            {r.suggestion && r.suggestion.code && r.suggestion_dismissed && (
                              <button onClick={() => dismissSuggestion(r, false)} data-testid={`recon-show-sug-${r.row_no}`}
                                className="text-[10px] text-[#6043EF] hover:underline text-left">↩ show LLM suggestion</button>
                            )}
                          </div>
                        )
                      ) : (
                        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          {r.resolved_code ? (
                            <a href={`/colleges/${encodeURIComponent(r.resolved_code)}`} target="_blank" rel="noopener noreferrer"
                              data-testid={`recon-college-link-${r.row_no}`}
                              className="text-[#6043EF] font-bold hover:underline hover:text-[#4A28C2] inline-flex items-center gap-0.5 shrink-0">
                              {r.resolved_code}<ArrowSquareOut size={11} weight="bold" className="opacity-60" />
                            </a>
                          ) : <span className="text-[#6043EF] font-bold">—</span>}
                          {r.resolved_name ? <span className="text-zinc-500 break-words min-w-0">{r.resolved_name}</span> : null}
                          {r.method === "manual" && (
                            <button onClick={() => assignCollege(r, null)} title="Clear manual assignment"
                              data-testid={`recon-unassign-${r.row_no}`}
                              className="ml-0.5 h-4 w-4 rounded-full flex items-center justify-center text-teal-600 hover:bg-teal-100">
                              <X size={11} weight="bold" />
                            </button>
                          )}
                          {(isSM || summary?.mode === "allotment") && r.resolved_code && r.method !== "manual" && !r.applied && (
                            <button onClick={() => setRemapRow(remapRow === r.row_no ? null : r.row_no)}
                              title="Re-map this college — it was auto-matched here; pick a different master and Apply to move its allotments/seats collision-safely"
                              data-testid={`recon-remap-${r.row_no}`}
                              className="ml-0.5 h-4 px-1 rounded flex items-center gap-0.5 text-[9px] font-bold text-amber-600 hover:bg-amber-100">
                              <PencilSimple size={11} weight="bold" />re-map
                            </button>
                          )}
                          {(isSM || summary?.mode === "allotment") && remapRow === r.row_no && (
                            <div className="w-full mt-1" data-testid={`recon-remap-box-${r.row_no}`}>
                              <ManualAssign onPick={(c) => { assignCollege(r, c); setRemapRow(null); }} testid={`recon-remap-assign-${r.row_no}`} />
                            </div>
                          )}
                        </span>
                      )}
                    </td>
                    {tab === "created" && (
                      <td className="px-2.5 py-1.5 whitespace-nowrap" data-testid={`recon-fill-status-${r.row_no}`}>
                        {(() => {
                          const fs = r.resolved_code ? fillStatuses[r.resolved_code] : null;
                          const st = fs?.status;
                          const stageOrder = { Places: 1, Website: 2, Details: 3, Media: 4 };
                          if (st === "completed") return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700"><Check size={12} weight="bold" /> enriched</span>;
                          if (st === "failed") return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600"><X size={12} weight="bold" /> failed</span>;
                          if (st === "running" || st === "processing" || st === "queued") {
                            const step = fs?.stage && stageOrder[fs.stage] ? `${fs.stage} ${stageOrder[fs.stage]}/4` : (st === "queued" ? "queued" : "enriching…");
                            return <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-sky-700"><SpinnerGap size={13} className="animate-spin" /> {step}</span>;
                          }
                          return <span className="text-[10px] text-zinc-400">—</span>;
                        })()}
                      </td>
                    )}
                    <td className={`px-2.5 py-1.5 align-top text-[#09090B] w-[320px] min-w-[320px] max-w-[320px] break-words ${skip ? "line-through" : ""}`}>
                      {(() => {
                        // The displayed name is the SHORT parsed name (e.g. "Faculty of Medicine"),
                        // which can be ambiguous to eyeball. When we have the full verbatim string
                        // from the sheet (college_name_raw), surface it on hover — dotted underline
                        // hints there's more.
                        const full = inp.college_name_raw && inp.college_name_raw !== inp.college_name
                          ? inp.college_name_raw : null;
                        return (
                          <span title={full || undefined}
                            data-testid={`recon-input-name-${r.row_no}`}
                            className={full ? "cursor-help underline decoration-dotted decoration-zinc-300 underline-offset-2" : undefined}>
                            {inp.college_name || inp.college_code || "—"}
                          </span>
                        );
                      })()}
                      {isOrphan ? (
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] font-bold" data-testid={`orphan-refs-${r.row_no}`}>
                          <span className="font-mono px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600" title="dangling college code (no master)">{r.orphan?.code || inp.code || "?"}</span>
                          {(r.orphan?.bodies || []).map((b) => (
                            <span key={b} className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700" title="counselling body (state vs MCC/central)">{b}</span>
                          ))}
                          {(() => {
                            const det = r.orphan?.scope_detail || {};
                            const sc = r.orphan?.scopes || {};
                            const labels = [["allotments", "allot", "text-zinc-500"], ["results", "state-cutoffs", "text-rose-600"], ["mcc_cutoff", "mcc-cutoffs", "text-rose-600"], ["cutoff", "predictor", "text-rose-600"], ["seat_matrix", "seats", "text-zinc-500"]];
                            return labels.filter(([k]) => (det[k]?.n ?? sc[k])).map(([k, lbl, cls]) => {
                              const yrs = (det[k]?.years || []).map((y) => `’${String(y).slice(-2)}`).join(" ");
                              const n = det[k]?.n ?? sc[k];
                              const tip = k === "results" ? "counselling_results — STATE historical cutoffs on the college-detail page"
                                : k === "mcc_cutoff" ? "mcc_cutoffs — MCC/AIQ historical cutoffs on the college-detail page"
                                : k === "cutoff" ? "cutoff_summary — the rank PREDICTOR aggregate (state + MCC)"
                                : k === "allotments" ? "raw_allotments — raw allotment rows (source of truth)"
                                : `${k} rows`;
                              return (
                                <span key={k} className={cls} title={tip}>{lbl} ×{n}{yrs ? ` ${yrs}` : ""}</span>
                              );
                            });
                          })()}
                        </div>
                      ) : (isAllot && inp.count ? <span className="ml-1.5 text-[10px] font-bold text-zinc-400" title="allotment rows for this name">×{inp.count}</span> : null)}
                    </td>
                    {isSM && (
                      <td className="px-2.5 py-1.5 align-top" data-testid={`sm-seatmatrix-${r.row_no}`}>
                        {(() => {
                          // NMC seat matrix = simple intake (no categories): show
                          // Σ intake + management + renew/increase deltas + new-est.
                          if (r.sm?.source_tag === "nmc_sm") {
                            const n = r.sm?.nmc || {};
                            const total = r.sm?.total_seats ?? n.mbbs ?? 0;
                            return (
                              <div className="flex flex-wrap items-center gap-1 max-w-[440px]">
                                <span className="px-1.5 py-0.5 rounded bg-[#6043EF] text-white text-[9px] font-black whitespace-nowrap" title="MBBS seat intake">Σ {total}</span>
                                {n.college_type && (
                                  <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[9px] font-bold whitespace-nowrap" title="management / college type">{n.college_type}</span>
                                )}
                                {!!n.seats_increased && (
                                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold whitespace-nowrap" title="seats increased vs previous year">+{n.seats_increased}</span>
                                )}
                                {!!n.seats_renewed && (
                                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold whitespace-nowrap" title="seats renewed">↻{n.seats_renewed}</span>
                                )}
                                {n.is_new_establishment && (
                                  <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[9px] font-black whitespace-nowrap" title="new establishment">NEW</span>
                                )}
                              </div>
                            );
                          }
                          const seats = r.sm?.seats || {};
                          const keys = Object.keys(seats).sort();
                          if (!keys.length) return <span className="text-zinc-300">—</span>;
                          const total = r.sm?.total_seats ?? keys.reduce((a, k) => a + (seats[k] || 0), 0);
                          return (
                            <div className="flex flex-wrap items-center gap-1 max-w-[440px]">
                              <span className="px-1.5 py-0.5 rounded bg-[#6043EF] text-white text-[9px] font-black whitespace-nowrap" title="total seats for this college">Σ {total}</span>
                              {keys.map((k) => (
                                <span key={k} className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[9px] font-bold whitespace-nowrap" title={`${k}: ${seats[k]} seats`}>
                                  {k}<span className="ml-0.5 text-[#6043EF]">{seats[k]}</span>
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {tab === "unmatched" && (
                      <td className="px-2.5 py-1.5 whitespace-nowrap">
                        <StateEditor value={r.state || ""} source={r.state_source}
                          onSave={(v) => setRowState(r, v)} onLlm={() => llmStateForRow(r)}
                          testid={`recon-state-${r.row_no}`} />
                      </td>
                    )}
                    {showYC && (
                      <td className="px-1.5 py-1">
                        <EditText value={cellVal(r, "course") ?? ""} disabled={skip} placeholder="MBBS"
                          onCommit={(v) => setField(r, "course", v)} testid={`recon-edit-course-${r.row_no}`} width="w-20" />
                      </td>
                    )}
                    {showYC && (
                      <td className="px-1.5 py-1 whitespace-nowrap">
                        <EditText value={cellVal(r, "year") ?? ""} disabled={skip} placeholder={CUR_YEAR}
                          emptyHint={`empty → ${CUR_YEAR} on Apply`}
                          onCommit={(v) => setField(r, "year", v)} testid={`recon-edit-year-${r.row_no}`} width="w-24" />
                      </td>
                    )}
                    {showSeats && (
                      <td className={`px-1.5 py-1 whitespace-nowrap ${!skip && (r.changed_fields || []).includes("seat_intake") ? "bg-amber-50" : ""}`}>
                        <EditText value={cellVal(r, "seat_intake") ?? ""} disabled={skip} placeholder="—"
                          emptyHint="top-level college intake (total_seats) — set on Apply"
                          onCommit={(v) => setField(r, "seat_intake", v)} testid={`recon-edit-intake-${r.row_no}`} width="w-16" />
                      </td>
                    )}
                    {showEst && (
                      <td className={`px-1.5 py-1 whitespace-nowrap ${!skip && (r.changed_fields || []).includes("established_year") ? "bg-amber-50" : ""}`}>
                        <EditText value={cellVal(r, "established_year") ?? ""} disabled={skip} placeholder="—"
                          onCommit={(v) => setField(r, "established_year", v)} testid={`recon-edit-est-${r.row_no}`} width="w-20" />
                      </td>
                    )}
                    {showStatus && (
                      <td className={`px-1.5 py-1 whitespace-nowrap ${!skip && (r.changed_fields || []).includes("college_status") ? "bg-amber-50" : ""}`}>
                        <EditText value={cellVal(r, "college_status") ?? ""} disabled={skip} placeholder="—"
                          onCommit={(v) => setField(r, "college_status", v)} testid={`recon-edit-status-${r.row_no}`} width="w-28" />
                      </td>
                    )}
                    {showFees && FEE_COLS.map((c) => {
                      const v = cellVal(r, c);
                      const filled = hasVal(v);
                      const isDragSrc = drag && drag.rowNo === r.row_no && drag.field === c;
                      const isDropOk = drag && drag.rowNo === r.row_no && drag.field !== c && !filled;
                      const isChanged = !skip && (r.changed_fields || []).includes(c);
                      return (
                        <td key={c}
                          draggable={filled && !skip}
                          onDragStart={filled && !skip ? (e) => { setDrag({ rowNo: r.row_no, field: c, value: v }); e.dataTransfer.effectAllowed = "move"; } : undefined}
                          onDragEnd={() => setDrag(null)}
                          onDragOver={isDropOk ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } : undefined}
                          onDrop={isDropOk ? (e) => { e.preventDefault(); moveCell(r, drag.field, c); } : undefined}
                          title={filled && !skip ? "Drag to move this fee into a blank column in the same row" : (isDropOk ? "Drop here" : undefined)}
                          data-testid={`recon-cell-${c}-${r.row_no}`}
                          className={`px-2.5 py-1.5 whitespace-nowrap ${skip ? "text-zinc-300 line-through" : ""} ${isChanged ? "bg-amber-100 text-amber-800 font-bold" : ""} ${filled && !skip ? "cursor-grab" : ""} ${isDragSrc ? "opacity-40" : ""} ${isDropOk ? "ring-2 ring-inset ring-[#6043EF]/50 bg-[#6043EF]/5" : ""}`}>
                          {fmtNum(v)}
                        </td>
                      );
                    })}
                    <td className="px-2.5 py-1.5 text-center">
                      <input type="checkbox" checked={skip} onChange={() => toggleSkip(r)}
                        title="Skip this row (won't be written on Apply)"
                        data-testid={`recon-skip-${r.row_no}`}
                        className="w-3.5 h-3.5 accent-rose-500 cursor-pointer" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="relative flex items-center justify-center gap-3 px-4 sm:px-6 py-2.5 border-t border-zinc-200 bg-white shrink-0">
        <span className="absolute left-4 sm:left-6 text-[11px] text-zinc-400 font-mono" data-testid="recon-page-info">
          {data.total} rows · page {page} / {totalPages}
        </span>
        <div className="flex items-center gap-1.5">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="recon-prev-page"
            className="h-8 w-8 rounded-full flex items-center justify-center border border-zinc-300 text-zinc-500 hover:bg-zinc-100 disabled:opacity-40">
            <CaretLeft size={14} weight="bold" />
          </button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="recon-next-page"
            className="h-8 w-8 rounded-full flex items-center justify-center border border-zinc-300 text-zinc-500 hover:bg-zinc-100 disabled:opacity-40">
            <CaretRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualAssign({ onPick, testid }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const r = await adminCollegesLiteSearch(query); setResults(r.data.results || []); setOpen(true); }
      catch (e) { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <div className="relative">
        <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="search & assign college…"
          data-testid={testid}
          className="w-56 h-7 pl-7 pr-2 rounded-full border border-rose-200 bg-rose-50/40 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40 focus:border-[#6043EF] focus:bg-white" />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-80 max-h-64 overflow-auto bg-white border border-zinc-200 rounded-xl shadow-xl" data-testid={`${testid}-results`}>
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-[12px] text-zinc-400"><SpinnerGap size={14} className="animate-spin" /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-zinc-400">No colleges found.</div>
          ) : results.map((c) => (
            <button key={c.college_code} onMouseDown={(e) => { e.preventDefault(); onPick(c); setQ(""); setOpen(false); }}
              data-testid={`${testid}-opt-${c.college_code}`}
              className="w-full text-left px-3 py-1.5 hover:bg-[#6043EF]/5 border-b border-zinc-50 last:border-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-mono font-bold text-[#6043EF]">{c.college_code}</span>
                <span className="text-[12px] text-[#09090B] truncate">{c.primary_name || c.short_name}</span>
              </div>
              {(c.city || c.state) && <div className="text-[10px] text-zinc-400">{[c.city, c.state].filter(Boolean).join(", ")}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SuggestionCell({ sug, onAccept, onDismiss, testid }) {
  const names = [sug.name, ...(sug.aliases || []).filter((a) => a && a !== sug.name)];
  const [idx, setIdx] = useState(0);
  const shown = names[idx % names.length] || sug.name;
  return (
    <div className="flex items-start gap-1.5 rounded-lg border border-[#6043EF]/30 bg-[#6043EF]/5 px-2 py-1.5 max-w-[300px]" data-testid={testid}>
      <Sparkle size={13} weight="fill" className="text-[#6043EF] mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <a href={`/colleges/${encodeURIComponent(sug.code)}`} target="_blank" rel="noopener noreferrer"
            className="text-[#6043EF] font-bold text-[11px] hover:underline shrink-0">{sug.code}</a>
          {names.length > 1 && (
            <button onClick={() => setIdx((i) => i + 1)} title="Show other known names"
              data-testid={`${testid}-cycle`} className="text-zinc-400 hover:text-[#6043EF] shrink-0">
              <ArrowClockwise size={11} weight="bold" />
            </button>
          )}
        </div>
        <div className="text-[11px] text-zinc-700 break-words leading-tight">{shown}</div>
        {sug.location && <div className="text-[10px] text-zinc-400 break-words leading-tight">{sug.location}</div>}
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button onClick={onAccept} title="Accept" data-testid={`${testid}-accept`}
          className="h-5 w-5 rounded flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white"><Check size={11} weight="bold" /></button>
        <button onClick={onDismiss} title="Dismiss" data-testid={`${testid}-dismiss`}
          className="h-5 w-5 rounded flex items-center justify-center bg-zinc-200 hover:bg-zinc-300 text-zinc-600"><X size={11} weight="bold" /></button>
      </div>
    </div>
  );
}

function StateEditor({ value, source, onSave, onLlm, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [llmBusy, setLlmBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);
  const save = () => { setEditing(false); if ((draft || "") !== (value || "")) onSave(draft || ""); };
  const runLlm = async () => { if (!onLlm || llmBusy) return; setLlmBusy(true); try { await onLlm(); } finally { setLlmBusy(false); } };
  if (editing) {
    return (
      <input ref={ref} list="recon-states-dl" value={draft} onChange={(e) => setDraft(e.target.value)}
        onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        data-testid={`${testid}-input`} placeholder="type a state…"
        className="h-7 w-44 text-[11px] rounded border border-[#6043EF]/50 px-1.5 focus:outline-none focus:ring-1 focus:ring-[#6043EF]/40" />
    );
  }
  const inferred = source === "auto" || source === "llm";
  return (
    <span className="inline-flex items-center gap-1 group">
      {value
        ? <span className={`text-[11px] ${inferred ? "text-amber-700" : "text-zinc-700"}`}>{value}</span>
        : <span className="text-[11px] text-rose-500">— no state —</span>}
      {value && source === "auto" && <span className="text-[9px] font-mono text-amber-500" title="Auto-classified (city→state) — please verify">auto</span>}
      {value && source === "llm" && <span className="text-[9px] font-mono text-indigo-500" title="Classified by AI — please verify">llm</span>}
      {!value && onLlm && (
        <button onClick={runLlm} disabled={llmBusy} data-testid={`${testid}-llm`}
          title="Ask AI to find the state for this college"
          className="inline-flex items-center gap-0.5 text-[9px] font-bold text-indigo-500 hover:text-indigo-700 disabled:opacity-50">
          {llmBusy ? <SpinnerGap size={11} weight="bold" className="animate-spin" /> : <Sparkle size={11} weight="fill" />}
          AI
        </button>
      )}
      <button onClick={() => { setDraft(value); setEditing(true); }} data-testid={testid}
        title="Edit state" className="text-zinc-300 hover:text-[#6043EF] group-hover:text-zinc-400">
        <PencilSimple size={12} weight="bold" />
      </button>
    </span>
  );
}


function EditText({ value, onCommit, disabled, placeholder, emptyHint, testid, width = "w-24" }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  const inputRef = useRef(null);
  useEffect(() => { setV(value ?? ""); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => { setEditing(false); if ((v ?? "") !== (value ?? "")) onCommit(v); };
  const cancel = () => { setV(value ?? ""); setEditing(false); };

  const shown = (value ?? "").toString().trim();
  const isEmpty = !shown;

  // READ-ONLY: value + pencil button (matches the College Detail edit pattern).
  if (!editing) {
    return (
      <div className={`${width} max-w-full flex items-center gap-1 group/edit`} data-testid={`${testid}-view`}>
        <span className={`flex-1 min-w-0 truncate text-[12px] ${disabled ? "text-zinc-300 line-through" : isEmpty ? "text-amber-500 italic" : "text-zinc-700"}`}
          title={isEmpty && emptyHint ? emptyHint : (shown || placeholder || "")}>
          {shown || placeholder || "—"}
        </span>
        {!disabled && (
          <button type="button" onClick={() => setEditing(true)} data-testid={testid}
            title="Edit"
            className="shrink-0 h-5 w-5 rounded flex items-center justify-center text-zinc-400 hover:text-[#6043EF] hover:bg-[#6043EF]/10 opacity-0 group-hover/edit:opacity-100 focus:opacity-100 transition-opacity">
            <PencilSimple size={13} weight="bold" />
          </button>
        )}
      </div>
    );
  }

  // EDITING: input + confirm/cancel; commit on blur/Enter, Esc cancels.
  return (
    <div className={`${width} max-w-full flex items-center gap-0.5`}>
      <input
        ref={inputRef}
        type="text" value={v} placeholder={placeholder}
        data-testid={`${testid}-input`}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        className="flex-1 min-w-0 h-7 px-1.5 rounded border text-[12px] bg-white border-[#6043EF] ring-1 ring-[#6043EF]/30 focus:outline-none text-zinc-700" />
      <button type="button" onMouseDown={(e) => { e.preventDefault(); commit(); }} data-testid={`${testid}-save`}
        title="Save" className="shrink-0 h-5 w-5 rounded flex items-center justify-center text-emerald-600 hover:bg-emerald-100">
        <Check size={13} weight="bold" />
      </button>
    </div>
  );
}

function FacetChip({ label, count, tone, active, onClick, testid }) {
  const tones = {
    indigo: active ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
    sky: active ? "bg-sky-600 text-white" : "bg-sky-50 text-sky-700 hover:bg-sky-100",
  };
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[10px] font-bold transition-colors ${tones[tone] || tones.indigo}`}>
      <span>{label}</span>
      <span className="opacity-70">({count})</span>
    </button>
  );
}

function Chip({ label, value, tone, active, onClick, testid }) {
  const tones = {
    zinc: "bg-zinc-100 text-zinc-600",
    emerald: "bg-emerald-100 text-emerald-700",
    rose: "bg-rose-100 text-rose-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
    violet: "bg-violet-100 text-violet-700",
  };
  const clickable = typeof onClick === "function";
  return (
    <button type="button" onClick={onClick} disabled={!clickable} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${tones[tone] || tones.zinc} ${clickable ? "cursor-pointer hover:brightness-95" : "cursor-default"} ${active ? "ring-2 ring-offset-1 ring-[#6043EF]" : ""}`}>
      <span className="uppercase tracking-wider opacity-70">{label}</span>
      <span>{value}</span>
    </button>
  );
}
